import React, { useEffect, useState, useCallback } from "react";
import styles from './story.module.css'

const Story = ({ stories, description, text, url, active }) => {
    const [showStories, setShowStories] = useState(false);
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [showDescription, setShowDescription] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const handleNext = useCallback(() => {
        if (currentStoryIndex === stories.length - 1) {
            setShowStories(false);
        } else {
            setProgress(0); // Reset immediately
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentStoryIndex(prevIndex => prevIndex + 1);
                setProgress(0);
                setIsTransitioning(false);
            }, 300);
        }
    }, [currentStoryIndex, stories.length]);

    // Auto-advance stories based on progress bar
    useEffect(() => {
        let progressInterval;
        if (showStories && !isTransitioning) {
            // Story duration: 3 seconds (3000ms)
            // Update progress every 50ms for smooth animation
            // Each update increases progress by ~1.67% (60 updates total = 3000ms)
            progressInterval = setInterval(() => {
                setProgress(prevProgress => {
                    if (prevProgress >= 100) {
                        handleNext();
                        return 0;
                    }
                    return prevProgress + (100 / 60); // 60 updates in 3 seconds
                });
            }, 50); // 50ms * 60 = 3000ms (3 seconds per story)
        } else {
            setProgress(0);
        }

        return () => {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
        };
    }, [showStories, currentStoryIndex, handleNext, isTransitioning]);

    const toggleDescription = () => {
        setShowDescription(prevState => !prevState);
    };

    const handleCloseStories = () => {
        setShowStories(false);
        setCurrentStoryIndex(0);
        setProgress(0);
    };

    const handleNavigate = (url) => {
        // history.push(link);
    };

    const handlePrev = () => {
        if (showStories) {
            if (currentStoryIndex === 0) {
                setProgress(0);
                return;
            }
            setProgress(0); // Reset immediately
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentStoryIndex(prevIndex => prevIndex === 0 ? 0 : prevIndex - 1);
                setProgress(0);
                setIsTransitioning(false);
            }, 300);
        }
    };

    const handleShowStories = () => {
        setCurrentStoryIndex(0);
        setShowStories(true);
    };

    // Prevent body scroll when stories are open and ensure fullscreen
    useEffect(() => {
        if (showStories) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.style.height = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.documentElement.style.overflow = '';
            document.documentElement.style.height = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.documentElement.style.overflow = '';
            document.documentElement.style.height = '';
        };
    }, [showStories]);

    return (
        <div style={{ position: 'relative' }}>
            <img
                style={{
                    objectFit: 'cover',
                    cursor: 'pointer',
                    marginRight: '15px',
                    width: '105px',
                    height: '105px',
                    borderRadius: '18px',
                    border: active ? '1px solid #EF2E25' : ''
                }}
                src={stories[0].url}
                alt=""
                onClick={handleShowStories}
            />
            {showStories && (
                <>
                    <div className={styles.storyBg} onClick={handleCloseStories}></div>
                    <div className={styles.storyContainer}>
                        <div className={styles.story}>
                            <img
                                key={currentStoryIndex}
                                className={`${styles.storyImage} ${styles.fadeIn}`}
                                src={stories[currentStoryIndex]?.url || stories[0]?.url}
                                alt="Story"
                                onError={(e) => {
                                    console.error('Story image failed to load:', stories[currentStoryIndex]?.url);
                                    e.target.style.display = 'none';
                                }}
                            />
                            
                            {/* Close button */}
                            <button 
                                className={styles.closeButton}
                                onClick={handleCloseStories}
                            >
                                ✕
                            </button>

                            {/* Progress bar */}
                            <div className={styles.progressContainer}>
                                <div 
                                    className={styles.progressBar}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>

                            {/* Navigation buttons */}
                            <div className={styles.buttonsContainer}>
                                <button 
                                    onClick={handlePrev} 
                                    className={styles.button}
                                    style={{ left: 0 }}
                                />
                                <button 
                                    onClick={handleNext} 
                                    className={styles.button}
                                    style={{ right: 0 }}
                                />
                            </div>

                            {/* Description toggle button */}
                            {url && (
                                <button
                                    onClick={toggleDescription}
                                    style={{
                                        position: 'absolute',
                                        bottom: '20px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        height: '40px',
                                        width: '40px',
                                        background: 'rgba(0, 0, 0, 0.5)',
                                        border: 'none',
                                        zIndex: '20',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {showDescription
                                        ? <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/></svg>
                                        : <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="M480-528 296-344l-56-56 240-240 240 240-56 56-184-184Z"/></svg>
                                    }
                                </button>
                            )}

                            {/* Description panel */}
                            {showDescription && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '0',
                                    height: '50%',
                                    width: '100%',
                                    padding: '20px',
                                    background: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    borderRadius: '20px 20px 0 0',
                                    zIndex: '15'
                                }}>
                                    <span dangerouslySetInnerHTML={{ __html: description }}></span>
                                    <a target='blank' href={url} style={{ display: 'flex', justifyContent: 'center', textDecoration: 'none' }}>
                                        <button
                                            onClick={() => handleNavigate(url)}
                                            style={{
                                                position: 'absolute',
                                                bottom: '60px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                cursor: 'pointer',
                                                color: '#EF2E25',
                                                borderRadius: '66px',
                                                width: '300px',
                                                height: '59px',
                                                background: '#fff',
                                                border: 'none',
                                                zIndex: '999',
                                                marginTop: '20px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {text ? text : 'Подробнее'}
                                        </button>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Story;